import React, { useState } from "react";
import MainLayout from "../../components/layout/MainLayout";
import { graphql } from "gatsby";
import { PageTemplateProps } from "../Page/PageTypes";
import { PageMetadataContext } from "../Page/PageContext";
import { getSeoProps } from "../Page/PageUtils";
import ArticleContent from "./ArticleContent";
import { formatLinks } from "../../lib/links";
import { formatVideo } from "../../lib/videos";
import { useUpdatePersonalization } from "../../lib/usePersonalization";

const ArticlePage: React.FC<PageTemplateProps> = ({
  data,
  pageContext,
  location,
  ...props
}) => {
  const elements = data?.kontentItemArticleContent?.elements || {};
  const systemData = data?.kontentItemArticleContent?.system || {};
  const pageData = data?.kontentItemPage?.elements;
  const [pageMetadata, setPageMetadata] = useState({
    slug: pageData?.url?.value || "",
    location: location,
    breadcrumbs: pageData?.breadcrumbs || [],
  });
  const seo = getSeoProps({ pageData, elements, systemData });
  const advice = formatLinks(elements?.advice?.value);
  const helpfulProducts = formatLinks(elements?.helpful_products?.value);
  const videos = elements?.videos?.value?.map((video) => {
    return formatVideo(video?.elements);
  });

  useUpdatePersonalization(pageData?.personalization);

  return (
    <MainLayout siteFrame={pageData?.site_frame} seo={seo} location={location}>
      <PageMetadataContext.Provider value={pageMetadata}>
        <ArticleContent
          pageTitle={pageData?.title?.value || ""}
          pageUrl={location.href}
          breadcrumbs={pageData?.breadcrumbs}
          content={elements?.content}
          advice={advice}
          helpfulProducts={helpfulProducts}
          videos={videos}
          moreNews={elements?.more_news?.value[0]?.elements}
        />
      </PageMetadataContext.Provider>
    </MainLayout>
  );
};

export default ArticlePage;

export const pageQuery = graphql`
  query ArticlePageById($pageId: String!, $contentId: String!) {
    kontentItemPage(id: { eq: $pageId }) {
      elements {
        breadcrumbs
        hide_breadcrumbs {
          value {
            codename
          }
        }
        url {
          url
          value
        }
        title {
          value
        }
        display_publish_date {
          value
        }
        site_frame {
          ...kontentSiteFrame
        }
        personalization {
          value {
            codename
          }
        }
      }
    }
    kontentItemArticleContent(id: { eq: $contentId }) {
      system {
        id
        codename
        last_modified
      }
      elements {
        page_metadata__page_description {
          value
        }
        page_metadata__canonical_url {
          value
        }
        page_metadata__robots_follow {
          value {
            codename
          }
        }
        page_metadata__robots_index {
          value {
            codename
          }
        }
        page_metadata__sitemap_include {
          value {
            codename
          }
        }
        page_metadata__social_share_description {
          value
        }
        page_metadata__social_share_image {
          value {
            ... on kontent_item_image {
              elements {
                image {
                  value {
                    url
                    width
                    height
                    description
                  }
                }
              }
            }
          }
        }
        page_metadata__social_share_title {
          value
        }
        page_metadata__twitter_card_type {
          value {
            codename
          }
        }

        advice {
          value {
            ...kontentRichTextLink
          }
        }
        helpful_products {
          value {
            ...kontentRichTextLink
          }
        }
        content {
          ...kontentModularRichText
        }
        videos {
          value {
            ...kontentVideo
          }
        }
        more_news {
          value {
            ...KnowledgeCenterListing
          }
        }
      }
    }
  }
`;
